<template>
    <Card class="p-4 mb-4" :header="$t('pluginCampaignsList.title')" :loading="loading && !filteredAvailableCampaigns.length">
        <ErrorMessage :value="error" class="mb-4" />

        <div class="d-flex flex-column">
            <div class="d-flex gap-5 flex-wrap">
                <div class="d-flex flex-column flex-grow-1 flex-basis-0">
                    <p class="mb-4">{{ $t('pluginCampaignsList.availableCampaigns') }}</p>
                    <div class="d-flex flex-column gap-3 max-h p-4">
                        <div v-for="campaign in filteredAvailableCampaigns" :key="campaign.id" class="d-flex align-items-center border-bottom py-2 gap-3">
                            <span>{{ campaign.name }}</span>
                            <ButtonIcon hide-label-while-loading variant="success" class="ml-auto" :loading="isSubmitting(campaign.id)" @click="addCampaign(campaign)">
                                {{ $t('add') }}
                            </ButtonIcon>
                        </div>
                        <div v-if="!filteredAvailableCampaigns.length" class="text-center py-3 text-muted">
                            {{ $t('pluginCampaignsList.noAvailableCampaigns') }}
                        </div>
                    </div>
                </div>

                <div class="d-flex flex-column flex-grow-1 flex-basis-0">
                    <p class="mb-4">{{ $t('pluginCampaignsList.activeCampaigns') }}</p>
                    <div class="d-flex flex-column gap-3 max-h p-4">
                        <div v-for="campaign in filteredActiveCampaigns" :key="campaign.id" class="d-flex align-items-center border-bottom py-2 gap-3">
                            <span class="flex-grow-1">{{ campaign.campaign.name }}</span>
                            <b-form-input v-model.number="campaign.priority" type="number" min="0" class="w-25" @blur="updateCampaign(campaign)" />
                            <ButtonIcon hide-label-while-loading variant="danger" class="ml-3" :loading="isSubmitting(campaign.campaign.id)" @click="removeCampaign(campaign)">
                                {{ $t('remove') }}
                            </ButtonIcon>
                        </div>
                        <div v-if="!filteredActiveCampaigns.length" class="text-center py-3 text-muted">
                            {{ $t('pluginCampaignsList.noActiveCampaigns') }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Card>
</template>

<script>
    import ButtonIcon from '@/components/common/ButtonIcon.vue';
    import pluginService from '@/services/pluginService';
    import ErrorMessage from '@/components/common/ErrorMessage.vue';

    export default {
        name: 'PluginCampaignsList',
        components: {
            ButtonIcon,
            ErrorMessage,
        },
        props: {
            plugin: {
                type: Object,
                required: true,
            },
        },
        data() {
            return {
                activeCampaigns: [],
                availableCampaigns: [],
                error: null,
                loading: false,

                createSubmitting: [],
                deleteSubmitting: [],
                updateSubmitting: [],
            };
        },
        computed: {
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },

            filteredAvailableCampaigns() {
                const campaignsArray = Object.values(this.availableCampaigns || {});
                return campaignsArray.filter(item => !this.filteredActiveCampaigns.some(active => active.campaign.id === item.id));
            },

            filteredActiveCampaigns() {
                const campaignsArray = Object.values(this.activeCampaigns || {});
                return campaignsArray;
            },
        },
        methods: {
            isSubmitting(id) {
                return this.createSubmitting.includes(id) || this.deleteSubmitting.includes(id) || this.updateSubmitting.includes(id);
            },
            async getData() {
                this.loading = true;
                await Promise.all([this.getActiveCampaigns(), this.getAvailableCampaigns()]);
                this.loading = false;
            },
            async getActiveCampaigns() {
                try {
                    this.error = null;
                    this.activeCampaigns = await pluginService.fetchActiveCampaigns(this.plugin.id);
                } catch (e) {
                    this.error = e.message;
                }
            },
            async getAvailableCampaigns() {
                try {
                    this.error = null;
                    this.availableCampaigns = await pluginService.fetchAvailableCampaigns(this.plugin.id);
                } catch (e) {
                    this.error = e.message;
                }
            },
            async addCampaign(campaign) {
                try {
                    this.error = null;
                    this.createSubmitting.push(campaign.id);

                    await pluginService.createCampaign(this.plugin.id, campaign.id);

                    await this.getData();
                    this.createSubmitting = this.createSubmitting.filter(id => id !== campaign.id);
                } catch (e) {
                    this.error = e.message;
                }
            },
            async removeCampaign(campaign) {
                try {
                    this.error = null;

                    this.deleteSubmitting.push(campaign.campaign.id);
                    await pluginService.deleteCampaign(this.plugin.id, campaign.campaign.id);

                    await this.getData();
                    this.deleteSubmitting = this.deleteSubmitting.filter(id => id !== campaign.campaign.id);
                } catch (e) {
                    this.error = e.message;
                }
            },
            async updateCampaign(campaign) {
                try {
                    this.error = null;
                    this.updateSubmitting.push(campaign.campaign.id);
                    await pluginService.updateCampaign(this.plugin.id, campaign.campaign.id, {
                        priority: campaign.priority,
                    });

                    await this.getData();
                    this.updateSubmitting = this.updateSubmitting.filter(id => id !== campaign.campaign.id);
                } catch (e) {
                    this.error = e.message;
                }
            },
        },
    };
</script>

<style scoped>
    .w-25 {
        width: 25% !important;
    }

    .max-h {
        max-height: 1300px;
        overflow-y: auto;
    }
</style>
